import { extendTheme } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 10px rgba(70, 255, 255, 0.3); }
  50% { box-shadow: 0 0 20px rgba(70, 255, 255, 0.5); }
  100% { box-shadow: 0 0 10px rgba(70, 255, 255, 0.3); }
`;

const theme = extendTheme({
  fonts: {
    heading: 'Montserrat, sans-serif', // Clear, readable font for headings
    body: 'Inter, sans-serif', // Highly legible font for body text
  },
  colors: {
    brand: {
      primary: '#0b3d91',
      accent: '#46ffff',
      dark: '#020814',
      light: '#ffffff',
      purple: '#9b4dca',
      pink: '#ff6b9d',
      darkBlue: '#0a192f',
      deepSpace: '#000814',
    },
  },
  styles: {
    global: {
      body: {
        bg: 'brand.dark',
        color: 'brand.light',
        lineHeight: 1.7,
        overflowX: 'hidden',
      },
      // Improved focus styles for better accessibility
      '*:focus': {
        outline: '2px solid',
        outlineColor: 'brand.accent',
        outlineOffset: '2px',
      },
      // Smooth scrolling for better user experience
      html: {
        scrollBehavior: 'smooth',
      },
      '.parallax-section': {
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(11, 61, 145, 0.2), rgba(155, 77, 202, 0.2))',
          transform: 'translateZ(-1px) scale(2)',
          zIndex: -1,
        },
      },
      '.cosmic-card': {
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(70, 255, 255, 0.1), transparent)',
          transform: 'translateX(-100%)',
          animation: `${shimmer} 3s infinite`,
        },
      },
      '.glow-effect': {
        animation: `${pulseGlow} 2s infinite`,
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        _focus: {
          boxShadow: '0 0 0 3px #46ffff',
        },
        position: 'relative',
        overflow: 'hidden',
        fontWeight: '500', // Better readability
        letterSpacing: '0.5px',
      },
      variants: {
        primary: {
          bg: 'rgba(11, 61, 145, 0.8)',
          color: 'white',
          backdropFilter: 'blur(8px)',
          _before: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '200%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(70, 255, 255, 0.2), transparent)',
            transition: 'all 0.5s',
          },
          _hover: {
            bg: 'brand.accent',
            color: 'brand.dark',
            transform: 'translateY(-2px)',
            _before: {
              left: '100%',
            },
          },
          _focus: {
            boxShadow: '0 0 0 3px #46ffff',
          },
          transition: 'all 0.3s',
        },
        outline: {
          border: '1px solid',
          borderColor: 'brand.accent',
          color: 'brand.accent',
          bg: 'transparent',
          backdropFilter: 'blur(8px)',
          _hover: {
            bg: 'rgba(70, 255, 255, 0.1)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    // Consistent card styling
    Card: {
      baseStyle: {
        container: {
          bg: 'rgba(11, 61, 145, 0.3)',
          backdropFilter: 'blur(8px)',
          borderRadius: 'xl',
          border: '1px solid',
          borderColor: 'brand.accent',
          transition: 'all 0.3s',
          _hover: {
            transform: 'translateY(-4px)',
            boxShadow: '0 4px 20px rgba(70, 255, 255, 0.2)',
          },
        },
      },
    },
    // Improved text readability
    Text: {
      baseStyle: {
        lineHeight: 'tall',
      },
    },
    Heading: {
      baseStyle: {
        letterSpacing: 'wide',
        lineHeight: 'shorter',
      },
    },
  },
});

export default theme;
